import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FeedbackPayload } from '../../services/api';

type PlusTrialSurveyProps = {
  onSubmit: (payload: FeedbackPayload) => Promise<unknown>;
};

const ALIAS_OPTIONS = ['5', '10', '20', 'unlimited'];

const PlusTrialSurvey = ({ onSubmit }: PlusTrialSurveyProps) => {
  const { t } = useTranslation();
  const [desiredAliases, setDesiredAliases] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const submit = async () => {
    setStatus('sending');
    try {
      await onSubmit({
        content: content.trim() || t('plusTrialSurvey.defaultFeedback'),
        metadata: {
          campaign: 'three_alias_plus_trial',
          desired_alias_count: desiredAliases || 'not_selected',
          current_alias_count: '3'
        }
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="mb-6 border-y border-slate-200 py-5" aria-labelledby="plus-trial-survey-title">
      <h3 id="plus-trial-survey-title" className="text-base font-semibold text-slate-900">
        {t('plusTrialSurvey.title')}
      </h3>
      <p className="mt-1 text-sm text-slate-600">{t('plusTrialSurvey.aliasQuestion')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {ALIAS_OPTIONS.map(option => (
          <button
            type="button"
            key={option}
            onClick={() => setDesiredAliases(option)}
            aria-pressed={desiredAliases === option}
            className={`rounded-md border px-3 py-2 text-sm ${desiredAliases === option ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 text-slate-700'}`}
          >
            {option === 'unlimited' ? t('plusTrialSurvey.unlimited') : t('plusTrialSurvey.aliasOption', { count: Number(option) })}
          </button>
        ))}
      </div>
      <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor="plus-trial-feedback">
        {t('plusTrialSurvey.detailsLabel')}
      </label>
      <textarea
        id="plus-trial-feedback"
        value={content}
        onChange={event => setContent(event.target.value)}
        rows={2}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
      />
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={status === 'sending' || status === 'sent'}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {status === 'sending' ? t('plusTrialSurvey.sending') : t('plusTrialSurvey.submit')}
        </button>
        {status === 'sent' && <span className="text-sm text-green-700">{t('plusTrialSurvey.success')}</span>}
        {status === 'error' && <span className="text-sm text-red-700">{t('plusTrialSurvey.error')}</span>}
      </div>
    </section>
  );
};

export default PlusTrialSurvey;
